/**
 * A basket whose legs do NOT expire together — ROADMAP 9.67.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE CURVE IS DRAWN AT THE NEAREST EXPIRY, NOT AT "EXPIRY"
 *
 * `basketPayoff.ts` settles every leg at one spot on one day. For a calendar
 * that is a specific, wrong claim: on the day the near leg settles, the far
 * leg is still an option with weeks of time value in it, and a chart that
 * zeroes it out draws a position nobody holds.
 *
 * So the horizon is the FIRST expiry in the basket, and on that date:
 *
 *   a leg expiring then   settles at intrinsic — exact, no model
 *   a leg expiring later  is REVALUED at its own implied volatility, with
 *                         the time it has left, by the same Black-76 the
 *                         chain's greeks and the margin scan already use
 *
 * This is what every options desk means by the payoff of a calendar, and it
 * is what the reference tools draw. The alternative — refusing to draw
 * anything — was the previous behaviour and is worse: the trader can see the
 * shape of a diagonal nowhere else.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * IT IS A MODEL VALUATION AND MUST BE READ AS ONE
 *
 * The near-leg half is arithmetic. The far-leg half is a price we computed,
 * and it is only as good as the volatility that went into it: if the surface
 * moves between now and the horizon — and across a calendar that is the whole
 * trade — the real curve will not be this one. The caller labels it with the
 * date it is drawn for, because "max profit ₹8L" means nothing without
 * "on 9 NOV, if vol is unchanged".
 *
 * A single-expiry basket must NOT come through here. `payoffShape` is exact
 * for that case, needs no volatility at all, and cannot disagree with the
 * order that follows it.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * CARRY IS SCALED, NOT HELD
 *
 * Each leg carries the forward actually observed for ITS expiry, which embeds
 * the real basis rather than an assumed rate. At the horizon only part of
 * that carry is left, so the ratio is taken to the power of the fraction of
 * the term remaining — which is the right thing under continuous
 * compounding, and degrades to "no basis" when a forward is missing.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE TAILS ARE SOLVED, NOT SAMPLED
 *
 * Far above every strike each call is deep in the money whatever its expiry,
 * so the slope is the net call quantity — a number, not a limit of samples.
 * `payoffShape` reports an uncovered short call as UNBOUNDED for this reason
 * and so does this, because the alternative is printing the loss at the edge
 * of whatever window was sampled, which is a figure that moves when the
 * window does and is exactly what someone sizes a position against.
 * ─────────────────────────────────────────────────────────────────────────────
 */
import { black76Price } from './greeks.js';
/** Nothing is left of a leg's life once less than ten minutes remain. */
const SETTLED = 1e-5;
/** How far either side of the money the curve is searched, as a fraction. */
const SCAN = 0.3;
const SAMPLES = 600;
const round2 = (v) => Math.round(v * 100) / 100;
const intrinsic = (leg, atSpot) => leg.optionType === 'CE'
    ? Math.max(0, atSpot - leg.strike)
    : Math.max(0, leg.strike - atSpot);
/** Years to the first expiry in the basket. */
export function nearestHorizon(legs) {
    let out = Infinity;
    for (const leg of legs)
        out = Math.min(out, Math.max(0, leg.years));
    return Number.isFinite(out) ? out : 0;
}
/**
 * What the basket is worth if spot is HERE on the horizon date.
 *
 * Exported because the chart samples it directly: drawing from a returned
 * array would fix the window at the moment the shape was computed, and the
 * chart owns its own window.
 */
export function valueAtHorizon(input, atSpot, horizonYears) {
    const { legs, spot, rate } = input;
    const at = Math.max(0, atSpot);
    let total = 0;
    for (const leg of legs) {
        const left = leg.years - horizonYears;
        if (!(left > SETTLED)) {
            total += (intrinsic(leg, at) - leg.avgPrice) * leg.netQty;
            continue;
        }
        /*
         * The part of this leg's carry that is still ahead of it. Taken from the
         * forward actually observed for its expiry, so the real basis is used
         * rather than an assumed rate; `**(left/years)` is the continuously
         * compounded share of it that has not yet accrued at the horizon.
         */
        const ratio = leg.forward > 0 && spot > 0 && leg.years > SETTLED
            ? (leg.forward / spot) ** (left / leg.years)
            : 1;
        const price = black76Price({
            forward: at * ratio,
            strike: leg.strike,
            years: left,
            rate,
            // Floored, never zero: a zero vol divides by zero inside the model.
            vol: Math.max(leg.iv, 0.0001),
            optionType: leg.optionType,
        });
        total += (price - leg.avgPrice) * leg.netQty;
    }
    return total;
}
/**
 * The slope far ABOVE every strike: every call is deep in the money there,
 * whatever its expiry, so this is the net call quantity exactly.
 */
function slopeAbove(legs) {
    let n = 0;
    for (const leg of legs)
        if (leg.optionType === 'CE')
            n += leg.netQty;
    return n;
}
/** The slope as spot approaches zero: every put is in the money. */
function slopeBelow(legs) {
    let n = 0;
    for (const leg of legs)
        if (leg.optionType === 'PE')
            n -= leg.netQty;
    return n;
}
/**
 * Extremes and breakevens of a calendar basket, on its nearest expiry.
 *
 * SAMPLED, unlike `payoffShape`, and that is forced rather than chosen: with
 * a far leg still alive the curve is smooth, so there is no finite set of
 * kinks that contains its extremes. The tails are still solved exactly —
 * see the header.
 */
export function calendarShape(input) {
    const { legs, spot } = input;
    const horizonYears = input.horizonYears ?? nearestHorizon(legs);
    if (legs.length === 0 || !(spot > 0)) {
        return {
            horizonYears,
            maxProfit: { kind: 'FINITE', value: 0, atSpot: spot },
            maxLoss: { kind: 'FINITE', value: 0, atSpot: spot },
            breakevens: [],
            slopeBelow: 0,
            slopeAbove: 0,
        };
    }
    /*
     * Wide enough to contain every strike AND the money, because an extreme
     * sits at a strike and a basket's strikes can be far from spot. Zero is
     * included: the left tail is bounded and spot-zero is often the real
     * worst case of a short put.
     */
    let lo = spot * (1 - SCAN);
    let hi = spot * (1 + SCAN);
    for (const leg of legs) {
        lo = Math.min(lo, leg.strike * (1 - SCAN));
        hi = Math.max(hi, leg.strike * (1 + SCAN));
    }
    lo = Math.max(0, lo);
    const at = (s) => valueAtHorizon(input, s, horizonYears);
    let best = { value: at(0), atSpot: 0 };
    let worst = best;
    const points = [{ spot: 0, value: best.value }];
    for (let i = 0; i <= SAMPLES; i += 1) {
        const s = lo + ((hi - lo) * i) / SAMPLES;
        const value = at(s);
        points.push({ spot: s, value });
        if (value > best.value)
            best = { value, atSpot: s };
        if (value < worst.value)
            worst = { value, atSpot: s };
    }
    const above = slopeAbove(legs);
    const below = slopeBelow(legs);
    /*
     * A root is found by SIGN CHANGE between neighbouring samples and placed
     * by linear interpolation inside that gap. The curve is smooth and the
     * samples are dense, so the error is far below the paisa this is rounded
     * to — and unlike a search it cannot wander past a root it has bracketed.
     */
    const found = [];
    for (let i = 1; i < points.length; i += 1) {
        const a = points[i - 1];
        const b = points[i];
        if (a.value === 0) {
            found.push(round2(a.spot));
            continue;
        }
        if (a.value < 0 === b.value < 0)
            continue;
        const span = b.value - a.value;
        found.push(round2(span === 0 ? a.spot : a.spot - (a.value * (b.spot - a.spot)) / span));
    }
    const breakevens = [...new Set(found)].sort((x, y) => x - y);
    return {
        horizonYears,
        maxProfit: above > 0
            ? { kind: 'UNBOUNDED', slope: above }
            : { kind: 'FINITE', value: round2(best.value), atSpot: round2(best.atSpot) },
        maxLoss: above < 0
            ? { kind: 'UNBOUNDED', slope: above }
            : { kind: 'FINITE', value: round2(worst.value), atSpot: round2(worst.atSpot) },
        breakevens,
        slopeBelow: below,
        slopeAbove: above,
    };
}
//# sourceMappingURL=basketCalendar.js.map